import React, { useEffect, useState } from "react";
import { TabView, TabPanel } from "primereact/tabview";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { ProgressSpinner } from "primereact/progressspinner";
import toast from "react-hot-toast";
import TinyMCEEditor from "../components/QuillEditor.jsx";
import DOMPurify from "dompurify";
import { useBlogs } from "../hooks/useBlog";
import { Select, Space } from "antd";
import { truncateText } from "../utils/authHelpers.js";
import { renderFieldLabel } from "../utils/formHelpers";
import { HideSortIcons } from "../utils/tableStyles";
const ManageBlog = () => {
  // State variables
  const {
    blogs,
    categories,
    labels,
    isCategoriesLoading,
    isLabelsLoading,
    addNewCategoryMutation,
    addNewLabelMutation,
    isLoading,
    addNewBlogMutation,
    updateBlogMutation,
    deleteBlogMutation,
    updateCategoryMutation,
    deleteCategoryMutation,
    deleteLabelMutation,
    updateLabelMutation,
  } = useBlogs();
  const [deletingCategoryId, setDeletingCategoryId] = useState(null);
  const [labelDeletingId, setLabelDeletingId] = useState(null);

  // Local state for categories and labels
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [labelOptions, setLabelOptions] = useState([]);

  useEffect(() => {
    setCategoryOptions(Array.isArray(categories) ? categories : []);
  }, [categories]);

  useEffect(() => {
    setLabelOptions(Array.isArray(labels) ? labels : []);
  }, [labels]);

  // Blog form state
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    category_id: null,
    label_ids: [],
    media: [],
  });

  // Modal visibility states
  const [blogModalVisible, setBlogModalVisible] = useState(false);
  const [categoryModalVisible, setCategoryModalVisible] = useState(false);
  const [labelModalVisible, setLabelModalVisible] = useState(false);

  // Editing states
  const [editingBlogId, setEditingBlogId] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingLabel, setEditingLabel] = useState(null);

  // Form input states
  const [categoryName, setCategoryName] = useState("");
  const [labelName, setLabelName] = useState("");

  const [previewMedia, setPreviewMedia] = useState(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [mediaError, setMediaError] = useState(""); // Add this state

  // Blog functions
  const deleteBlog = async (id) => {
    await deleteBlogMutation.mutateAsync(id);
  };

  const saveBlog = async () => {
    if (!formData.title || !formData.content) {
      toast.error("Title and content are required!");
      return;
    }

    const form = new FormData();
    form.append("title", formData.title);
    form.append("content", formData.content);
    form.append("category_id", formData.category_id);
    (formData.label_ids || []).forEach((label) =>
      form.append("label_ids", label)
    );
    (formData.media || []).forEach((file) => form.append("media", file));

    if (editingBlogId) {
      await updateBlogMutation.mutateAsync({
        id: editingBlogId,
        blogData: form,
      });
    } else {
      await addNewBlogMutation.mutateAsync(form);
    }
  };

  // Category functions
  const saveCategory = async () => {
    if (!categoryName.trim()) {
      toast.error("Category name cannot be empty");
      return;
    }
    if (editingCategory) {
      await updateCategoryMutation.mutateAsync({
        id: editingCategory.id,
        categoryData: { name: categoryName },
      });
    } else {
      await addNewCategoryMutation.mutateAsync({ name: categoryName });
    }
  };

  const openCategoryModal = (category = null) => {
    setEditingCategory(category);
    setCategoryName(category?.name || "");
    setCategoryModalVisible(true);
  };

  const deleteCategory = async (id) => {
    setDeletingCategoryId(id); // Set the ID of the row being deleted
    await deleteCategoryMutation.mutateAsync(id);
  };

  // Label functions
  const saveLabel = async () => {
    if (!labelName.trim()) {
      toast.error("Label name cannot be empty");
      return;
    }
    if (editingLabel) {
      await updateLabelMutation.mutateAsync({
        id: editingLabel.id,
        labelData: { name: labelName },
      });
      setLabelModalVisible(false);
    } else {
      await addNewLabelMutation.mutateAsync({ name: labelName });
    }
  };

  const openLabelModal = (label = null) => {
    setEditingLabel(label);
    setLabelName(label?.name || "");
    setLabelModalVisible(true);
  };

  const deleteLabel = async (id) => {
    setLabelDeletingId(id);
    await deleteLabelMutation.mutateAsync(id);
  };

  const truncateContent = (content) => {
    const cleanContent = DOMPurify.sanitize(content, { ALLOWED_TAGS: [] });
    return cleanContent.length > 100
      ? cleanContent.substring(0, 100) + "..."
      : cleanContent;
  };

  useEffect(() => {
    if (addNewBlogMutation.isSuccess || updateBlogMutation.isSuccess) {
      setFormData({
        title: "",
        content: "",
        category_id: null,
        label_ids: [],
        media: [],
      });
      setBlogModalVisible(false);
      setEditingBlogId(null);
    }
    if (addNewCategoryMutation.isSuccess || updateCategoryMutation.isSuccess) {
      setCategoryName("");
      setCategoryModalVisible(false);
      setEditingCategory(null);
    }
    if (addNewLabelMutation.isSuccess || updateLabelMutation.isSuccess) {
      setLabelName("");
      setLabelModalVisible(false);
      setEditingLabel(null);
    }

    if (deleteCategoryMutation.isSuccess) {
      setDeletingCategoryId(null);
      setDeletingCategoryId(null);
    }
    if (deleteLabelMutation.isSuccess) {
      setLabelDeletingId(null);
    }
  }, [
    addNewBlogMutation.isSuccess,
    updateBlogMutation.isSuccess,
    updateCategoryMutation.isSuccess,
    addNewCategoryMutation.isSuccess,
    addNewLabelMutation.isSuccess,
    deleteCategoryMutation.isSuccess,
    deleteBlogMutation.isSuccess,
    deleteLabelMutation.isSuccess,
  ]);

  const renderFooter = (name) => {
    return (
      <div className="flex gap-4 justify-end">
        <Button
          label="Cancel"
          icon="pi pi-times"
          onClick={() => {
            if (name === "blog") setBlogModalVisible(false);
            if (name === "category") setCategoryModalVisible(false);
            if (name === "label") setLabelModalVisible(false);
          }}
          className="bg-[#BA1500] text-white btn text-[10px] lg:text-sm focus:outline-none focus:ring-0"
        />
        <Button
          label="Save"
          icon="pi pi-check"
          onClick={() => {
            if (name === "blog") saveBlog();
            if (name === "category") saveCategory();
            if (name === "label") saveLabel();
          }}
          autoFocus
          loading={
            (name === "blog" &&
              (addNewBlogMutation.isPending || updateBlogMutation.isPending)) ||
            (name === "category" &&
              (addNewCategoryMutation.isPending ||
                updateCategoryMutation.isPending)) ||
            (name === "label" &&
              (addNewLabelMutation.isPending || updateLabelMutation.isPending))
          }
          disabled={
            (name === "blog" &&
              (addNewBlogMutation.isPending || updateBlogMutation.isPending)) ||
            (name === "category" &&
              (addNewCategoryMutation.isPending ||
                updateCategoryMutation.isPending)) ||
            (name === "label" &&
              (addNewLabelMutation.isPending || updateLabelMutation.isPending))
          }
          className="bg-[#BA1500] text-white btn text-[10px] lg:text-sm focus:outline-none focus:ring-0"
        />
      </div>
    );
  };

  if ((isCategoriesLoading, isLabelsLoading, isLoading)) {
    return (
      <div className="flex justify-center items-center h-screen">
        <ProgressSpinner />
      </div>
    );
  }

  return (
    <div className="p-5">
      <HideSortIcons />
      <div className="flex justify-between">
        <h1 className="lg:text-2xl font-semibold text-[#003049] mb-5">
          News management
        </h1>
        <div>
          <Button
            label="Add News"
            icon="pi pi-plus"
            className="bg-[#BA1500] text-white btn text-sm focus:outline-none focus:ring-0"
            onClick={() => {
              setFormData({
                title: "",
                content: "",
                category_id: null,
                label_ids: [],
                media: [],
              });
              setEditingBlogId(null);
              setBlogModalVisible(true);
            }}
          />
        </div>
      </div>

      <TabView
        className="w-full"
        panelContainerClassName="p-4 bg-white rounded-b-lg shadow"
      >
        <TabPanel
          header="News"
          headerClassName="text-gray-700"
          headerTemplate={(options) => (
            <button
              type="button"
              onClick={options.onClick}
              className={`px-4 py-2 text-sm font-medium border-b-2 ${
                options.selected
                  ? "border-[#003049] text-[#003049]"
                  : "border-transparent text-gray-500 hover:text-[#003049] hover:border-blue-300"
              } focus:outline-none`}
            >
              {options.titleElement}
            </button>
          )}
        >
          <DataTable
            value={blogs}
            paginator
            rows={5}
            responsiveLayout="scroll"
            sortMode="single"
            showSortIcon={false}
            className="text-xs sm:text-sm md:text-base"
            onRowDoubleClick={(e) => {
              setEditingBlogId(e.data.id);
              setFormData({
                ...e.data,
                media: e.data.images.concat(e.data.videos || []),
              });
              setBlogModalVisible(true);
            }}
            loading={isLoading}
            emptyMessage="No blogs found"
            rowClassName={"cursor-pointer"}
          >
            <Column
              header="Action"
              headerClassName="bg-[#003049] text-white capitalize"
              style={{ width: "5%" }}
              body={(rowData) => (
                <div className="flex gap-5">
                  <i
                    className="pi pi-pencil text-blue-600 hover:text-blue-800 cursor-pointer"
                    onClick={() => {
                      setEditingBlogId(rowData.id);
                      setFormData({
                        ...rowData,
                        media: [],
                      });
                      setBlogModalVisible(true);
                    }}
                  />
                </div>
              )}
            ></Column>
            <Column
              field="title"
              header="Title"
              headerClassName="bg-[#003049] text-white capitalize"
              className=" text-left max-w-[120px] truncate"
              style={{ width: "15%" }}
              body={(rowData) => (
                <span title={rowData.title}>
                  {truncateText(rowData.title, 25)}
                </span>
              )}
              sortable
            />
            <Column
              field="media"
              header="Media"
              headerClassName="bg-[#003049] text-white capitalize"
              className="text-center"
              style={{ width: "90px" }}
              body={(rowData) => {
                const img =
                  rowData.images && rowData.images.length > 0
                    ? rowData.images[0]
                    : null;
                const vid =
                  rowData.videos && rowData.videos.length > 0
                    ? rowData.videos[0]
                    : null;
                if (img) {
                  return (
                    <img
                      src={img.image_url || img.url}
                      alt="blog-media"
                      className="w-14 h-14 object-cover rounded cursor-pointer"
                      onClick={() => {
                        setPreviewMedia({
                          type: "image",
                          src: img.image_url || img.url,
                        });
                        setPreviewVisible(true);
                      }}
                    />
                  );
                }
                if (vid) {
                  return (
                    <video
                      src={vid.video_url || vid.url}
                      className="w-14 h-14 object-cover rounded cursor-pointer"
                      style={{ background: "#eee" }}
                      controls={false}
                      muted
                      onClick={() => {
                        setPreviewMedia({
                          type: "video",
                          src: vid.video_url || vid.url,
                        });
                        setPreviewVisible(true);
                      }}
                    />
                  );
                }
                return null;
              }}
            />
            <Column
              field="content"
              header="Content"
              headerClassName="bg-[#003049] text-white capitalize"
              className=" text-left max-w-[160px] truncate"
              style={{ width: "25%" }}
              body={(rowData) => (
                <span
                  title={DOMPurify.sanitize(rowData.content, {
                    ALLOWED_TAGS: [],
                  })}
                >
                  {truncateText(
                    DOMPurify.sanitize(rowData.content, { ALLOWED_TAGS: [] }),
                    40
                  ) || "N/A"}
                </span>
              )}
            />
            <Column
              field="category.name"
              header="Category"
              headerClassName="bg-[#003049] text-white capitalize"
              className=" text-left max-w-[80px] truncate"
              style={{ width: "25%" }}
              body={(rowData) => (
                <span title={rowData.category?.name}>
                  {truncateText(rowData.category?.name, 15) || "N/A"}
                </span>
              )}
            />
            <Column
              field="labels"
              header="Labels"
              style={{ width: "10%" }}
              headerClassName="bg-[#003049] text-white capitalize"
              className=" text-left max-w-[100px] truncate"
              body={(rowData) =>
                Array.isArray(rowData.labels) ? (
                  <span
                    title={rowData.labels
                      .map((label) => label.label.name)
                      .join(", ")}
                  >
                    {truncateText(
                      rowData.labels
                        .map((label) => label.label.name)
                        .join(", "),
                      20
                    ) || <span className="uppercase">N/A</span>}
                  </span>
                ) : (
                  ""
                )
              }
            />
            <Column
              header="Action"
              headerClassName="bg-[#003049] text-white capitalize"
              style={{ width: "10%" }}
              body={(rowData) => (
                <div className="flex gap-5">
                  <i
                    className="pi pi-trash text-red-600 hover:text-red-800 cursor-pointer"
                    onClick={() => deleteBlog(rowData.id)}
                    loading={
                      rowData.id === deletingCategoryId
                        ? deleteBlogMutation.isPending
                        : null
                    } // Show loading only for the specific row
                  />
                </div>
              )}
            ></Column>
          </DataTable>
        </TabPanel>

        <TabPanel
          header="Categories & labels"
          headerClassName="text-gray-700"
          headerTemplate={(options) => (
            <button
              type="button"
              onClick={options.onClick}
              className={`px-4 py-2 text-sm font-medium border-b-2 ${
                options.selected
                  ? "border-[#003049] text-[#003049]"
                  : "border-transparent text-gray-500 hover:text-[#003049] hover:border-blue-300"
              } focus:outline-none`}
            >
              {options.titleElement}
            </button>
          )}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Categories Section */}
            <div className="card p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Categories</h3>
                <Button
                  label="Add Category"
                  icon="pi pi-plus"
                  className="bg-[#BA1500] text-white btn text-sm"
                  onClick={() => openCategoryModal()}
                />
              </div>
              <DataTable
                value={categories}
                paginator
                rows={5}
                sortMode="single"
                showSortIcon={false}
                loading={isCategoriesLoading}
              >
                <Column
                  field="name"
                  header="Name"
                  headerClassName="bg-[#003049] text-white"
                  sortable
                ></Column>
                <Column
                  header="Actions"
                  headerClassName="bg-[#003049] text-white"
                  body={(rowData) => (
                    <div className="flex gap-2">
                      <Button
                        icon="pi pi-pencil"
                        className="p-button-warning p-button-sm"
                        onClick={() => openCategoryModal(rowData)}
                      />
                      <Button
                        icon="pi pi-trash"
                        className="p-button-danger p-button-sm"
                        onClick={() => deleteCategory(rowData.id)}
                        loading={deletingCategoryId === rowData.id} // Show loading only for the specific row
                      />
                    </div>
                  )}
                ></Column>
              </DataTable>
            </div>

            {/* Labels Section */}
            <div className="card p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Labels</h3>
                <Button
                  label="Add Label"
                  icon="pi pi-plus"
                  className="bg-[#BA1500] text-white btn text-sm"
                  onClick={() => openLabelModal()}
                />
              </div>
              <DataTable
                value={labels}
                paginator
                rows={5}
                sortMode="single"
                showSortIcon={false}
                loading={isLabelsLoading}
              >
                <Column
                  field="name"
                  header="Name"
                  headerClassName="bg-[#003049] text-white"
                  sortable
                ></Column>
                <Column
                  header="Actions"
                  headerClassName="bg-[#003049] text-white"
                  body={(rowData) => (
                    <div className="flex gap-2">
                      <Button
                        icon="pi pi-pencil"
                        className="p-button-warning p-button-sm"
                        onClick={() => openLabelModal(rowData)}
                      />
                      <Button
                        icon="pi pi-trash"
                        className="p-button-danger p-button-sm"
                        onClick={() => deleteLabel(rowData.id)}
                        loading={labelDeletingId === rowData.id} // Show loading only for the specific row
                      />
                    </div>
                  )}
                ></Column>
              </DataTable>
            </div>
          </div>
        </TabPanel>
      </TabView>

      {/* Blog Modal */}
      <Dialog
        header={editingBlogId ? "Edit blog" : "Add blog"}
        visible={blogModalVisible}
        onHide={() => setBlogModalVisible(false)}
        footer={renderFooter("blog")}
        className="w-full lg:w-[50%]"
      >
        <div className="mx-auto space-y-4">
          <div className="field">
            <label>
              Title <span className="text-red-500 ml-1">*</span>
            </label>
            <InputText
              name="title"
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              className="w-full border focus:outline-none focus:ring-0 p-2"
            />
          </div>
          <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200">
            <p className="text-gray-500 text-sm">
              Use the picture upload function in the editor header to add pictures into body of the news article.
            </p>
          </div>
          <div className="field">
            <label>
              Content <span className="text-red-500 ml-1">*</span>
            </label>
            <TinyMCEEditor
              key={editingBlogId ? `edit-${editingBlogId}` : "new"}
              className="w-full border focus:outline-none focus:ring-0 p-2"
              value={formData.content}
              onChange={(html) => setFormData({ ...formData, content: html })}
            />
          </div>
          <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200">
            <div className="mb-4">
              <label className="block font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                name="category_id"
                value={formData.category_id ? String(formData.category_id) : ""}
                onChange={(e) =>
                  setFormData({ ...formData, category_id: e.target.value })
                }
                className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-blue-200 focus:border-blue-400 bg-white"
              >
                <option value="" disabled>
                  Select a category
                </option>
                {categoryOptions.map((cat) => (
                  <option key={String(cat.id)} value={String(cat.id)}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-2">
              <label className="block font-medium text-gray-700 mb-1">
                Labels
              </label>
              <select
                name="label_ids"
                multiple
                value={formData.label_ids ? formData.label_ids.map(String) : []}
                onChange={(e) => {
                  const selected = Array.from(e.target.selectedOptions).map(
                    (opt) => opt.value
                  );
                  setFormData({ ...formData, label_ids: selected });
                }}
                className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-blue-200 focus:border-blue-400 bg-white"
                size={Math.min(5, labelOptions.length) || 2}
              >
                {labelOptions.map((label) => (
                  <option key={String(label.id)} value={String(label.id)}>
                    {label.name}
                  </option>
                ))}
              </select>
              <div className="text-xs text-gray-500 mt-1">
                Hold <span className="font-semibold">Ctrl</span> (Windows) or{" "}
                <span className="font-semibold">Cmd</span> (Mac) to select
                multiple labels. On mobile, tap to select/deselect.
              </div>
            </div>
            <div className="mt-2 text-sm text-blue-700">
              {formData.label_ids && formData.label_ids.length > 0 ? (
                <>
                  Selected ({formData.label_ids.length}):{" "}
                  {labelOptions
                    .filter((label) =>
                      formData.label_ids.includes(String(label.id))
                    )
                    .map((label) => label.name)
                    .join(", ")}
                </>
              ) : (
                <span className="text-gray-400">No labels selected</span>
              )}
            </div>
          </div>
          <div className="field">
            <label>Upload images or video (this image/video is for the news title)</label>
            <input
              type="file"
              accept="image/*,video/*"
              multiple
              onChange={(e) => {
                const files = Array.from(e.target.files);
                const maxFiles = 5;
                const maxSize = 100 * 1024 * 1024; // 100MB
                // Allowed extensions
                const allowedExtensions = [
                  "jpg",
                  "jpeg",
                  "png",
                  "gif",
                  "webp", // images
                  "mp4",
                  "mov",
                  "avi",
                  "webm", // videos
                ];
                let error = "";

                if (files.length > maxFiles) {
                  error = `You can upload up to ${maxFiles} files.`;
                } else {
                  for (let file of files) {
                    const ext = file.name.split(".").pop().toLowerCase();
                    if (!allowedExtensions.includes(ext)) {
                      error =
                        "Only image (jpg, jpeg, png, gif, webp) and video (mp4, mov, avi, webm) files are allowed.";
                      break;
                    }
                    if (file.size > maxSize) {
                      error = "Each file must be less than 10MB.";
                      break;
                    }
                  }
                }

                setMediaError(error);
                if (!error) {
                  setFormData({ ...formData, media: files });
                }
              }}
              className="block w-full border p-2"
            />
            {mediaError && (
              <div className="text-red-600 text-sm mt-2">{mediaError}</div>
            )}
          </div>
          {formData.media && formData.media.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {Array.from(formData.media).map((file, idx) => {
                let src = "";
                if (file instanceof File || file instanceof Blob) {
                  src = file.objectURL || URL.createObjectURL(file);
                  file.objectURL = src;
                } else if (file.url) {
                  src = file.url;
                }
                if (!src) return null;

                const isVideo =
                  file.type?.startsWith("video") ||
                  (file.url && file.url.match(/\.(mp4|webm|ogg)$/i));

                return (
                  <div key={idx} className="relative group">
                    {isVideo ? (
                      <video
                        src={src}
                        controls
                        className="w-32 h-20 object-cover rounded"
                      />
                    ) : (
                      <img
                        src={src}
                        alt={`preview-${idx}`}
                        className="w-20 h-20 object-cover rounded"
                      />
                    )}
                    <button
                      type="button"
                      className="absolute top-1 right-1 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-80 hover:opacity-100 transition"
                      onClick={() => {
                        const newMedia = formData.media.filter(
                          (_, i) => i !== idx
                        );
                        setFormData({ ...formData, media: newMedia });
                      }}
                      title="Remove"
                    >
                      &times;
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          {editingBlogId && formData.media && formData.media.length === 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {/* Show existing images */}
              {blogs
                .find((b) => b.id === editingBlogId)
                ?.images?.map((img, idx) => (
                  <img
                    key={`img-${idx}`}
                    src={img.image_url || img.url}
                    alt={`existing-img-${idx}`}
                    className="w-20 h-20 object-cover rounded"
                  />
                ))}
              {/* Show existing videos */}
              {blogs
                .find((b) => b.id === editingBlogId)
                ?.videos?.map((vid, idx) => (
                  <video
                    key={`vid-${idx}`}
                    src={vid.video_url || vid.url}
                    controls
                    className="w-32 h-20 object-cover rounded"
                  />
                ))}
            </div>
          )}
        </div>
      </Dialog>

      {/* Category Modal */}
      <Dialog
        header={editingCategory ? "Edit category" : "Add category"}
        visible={categoryModalVisible}
        onHide={() => setCategoryModalVisible(false)}
        footer={renderFooter("category")}
      >
        {addNewCategoryMutation.isPending ||
        updateCategoryMutation.isPending ? (
          <div className="flex justify-center items-center h-32">
            <ProgressSpinner />
          </div>
        ) : (
          <div className="field">
            <label>Category name</label>
            <InputText
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
              className="w-full mt-4 border p-2"
              autoFocus
            />
          </div>
        )}
      </Dialog>

      {/* Label Modal */}
      <Dialog
        header={editingLabel ? "Edit label" : "Add label"}
        visible={labelModalVisible}
        onHide={() => setLabelModalVisible(false)}
        footer={renderFooter("label")}
      >
        {addNewLabelMutation.isPending || updateLabelMutation.isPending ? (
          <div className="flex justify-center items-center h-32">
            <ProgressSpinner />
          </div>
        ) : (
          <div className="field">
            <label>Label name</label>
            <InputText
              value={labelName}
              onChange={(e) => setLabelName(e.target.value)}
              className="w-full mt-4 border p-2"
              autoFocus
            />
          </div>
        )}
      </Dialog>

      {/* Media Preview Modal */}
      <Dialog
        header="Preview"
        visible={previewVisible}
        onHide={() => setPreviewVisible(false)}
        style={{ width: "50vw", maxWidth: 600 }}
        closable
      >
        {previewMedia && previewMedia.type === "image" && (
          <img
            src={previewMedia.src}
            alt="preview"
            className="w-full h-auto rounded"
            style={{ maxHeight: 400, objectFit: "contain" }}
          />
        )}
        {previewMedia && previewMedia.type === "video" && (
          <video
            src={previewMedia.src}
            controls
            autoPlay
            className="w-full h-auto rounded"
            style={{ maxHeight: 400, objectFit: "contain" }}
          />
        )}
      </Dialog>
    </div>
  );
};

export default ManageBlog;
